/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Wade Wilkey <wade.wilkey@deliciousai.com>
 */

import { FormatIcu } from '@tolgee/format-icu'
import { BackendFetch, DevTools, Tolgee } from '@tolgee/react'
import dayjs from 'dayjs'

const {
  VITE_APP__TOLGEE_API_KEY,
  VITE_APP__TOLGEE_CDN,
  VITE_APP__TENANT_NAME,
} = import.meta.env

export const tolgee = Tolgee()
  // DevTools automatically get removed in production
  .use(DevTools())
  .use(
    BackendFetch({
      prefix: VITE_APP__TOLGEE_CDN,
    }),
  )
  .use(FormatIcu())
  .init({
    // TODO: Let user select a language or detect the device language
    language: VITE_APP__TENANT_NAME ? `en-${VITE_APP__TENANT_NAME}` : 'en',
    defaultLanguage: 'en',
    fallbackLanguage: 'en',

    // Only used in Development mode
    apiUrl: 'https://app.tolgee.io',
    apiKey: VITE_APP__TOLGEE_API_KEY,
  })

export type Language = 'en' | 'es'

const LANGUAGE_STORAGE_KEY = 'DAI_LANGUAGE'

export const LanguageManager = {
  getCurrentLanguage: () => {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY)
  },
  setCurrentLanguage: (lang: Language) => {
    tolgee.changeLanguage(lang)
    dayjs.locale(lang)
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
  },
  initializeLanguage: () => {
    const savedLanguage = LanguageManager.getCurrentLanguage()
    if (savedLanguage) {
      tolgee.changeLanguage(savedLanguage)
      dayjs.locale(savedLanguage)
    } else {
      const userLang = navigator.language
      const languageFirst = userLang.split('-')[0]
      tolgee.changeLanguage(languageFirst)
    }
  },
}

LanguageManager.initializeLanguage()

export const formatCurrency = (
  value: number,
  options?: Intl.NumberFormatOptions,
) => {
  const language = tolgee.getLanguage()
  const currency = 'USD' // TODO: how do we dynamically set this????
  return `${value.toLocaleString(language, { maximumFractionDigits: 0, currency, style: 'currency', ...options })}`
}

export const formatNumber = (
  value: number,
  options?: Intl.NumberFormatOptions,
) => {
  const language = tolgee.getLanguage()
  return `${value.toLocaleString(language, { ...options })}`
}

// Tolgee in the web adds this character at the end to allow for dev
// tools to find tolgee keys...we strip them for colors
export const formatColor = (value: string): string =>
  value.replace(/[\u200B-\u200D\uFEFF]/g, '')
